import { useMemo, useState } from 'react';
import { Button, Card, CardContent, CardHeader, Tooltip } from '@mui/material';
import { DeleteForever, Edit, PlaylistAdd } from '@mui/icons-material';
import { useAppTranslation } from '../../services/i18n';
import {
  createCol,
  DataGrid,
  DataGridCol,
  DataGridMode,
  DataGridState,
  isSearchWithinSubject,
  ItemsState,
  OrderDirType,
} from '../DataGrid';
import { UserLabel } from '../../generated-api';
import { UserLabelColorSwatch } from './UserLabelColorSwatch';
import { UserLabelSaveForm } from './UserLabelSaveForm';
import { UserLabelDeleteForm } from './UserLabelDeleteForm';

interface UserLabelFilter {
  search?: string;
}

const filterCallback = (filter: UserLabelFilter, item: UserLabel): boolean => {
  return isSearchWithinSubject(filter.search, item.name);
};

const defaultState: DataGridState<UserLabel, UserLabelFilter> = {
  orderCol: 'name',
  orderDir: OrderDirType.ASC,
  filter: {
    search: '',
  },
  filterCallback,
};

type Props = {
  itemsState: ItemsState<UserLabel>;
  fetchItems: () => unknown;
};

export const UserLabelList = (props: Props) => {
  const { itemsState, fetchItems } = props;

  const t = useAppTranslation();
  const [isOpenAddForm, setIsOpenAddForm] = useState(false);
  const [itemForEditForm, setItemForEditForm] = useState<UserLabel | null>(null);
  const [itemForDeleteDialog, setItemForDeleteDialog] = useState<UserLabel | null>(null);

  const [dataGridState, setDataGridState] = useState<DataGridState<UserLabel, UserLabelFilter>>(defaultState);

  const handleOpenAddForm = () => {
    setIsOpenAddForm(true);
  };

  const handleCloseAddForm = () => {
    setIsOpenAddForm(false);
  };

  const handleOpenEditForm = (item: UserLabel) => {
    setItemForEditForm(item);
  };

  const handleCloseEditForm = () => {
    setItemForEditForm(null);
  };

  const handleOpenDeleteDialog = (item: UserLabel) => {
    setItemForDeleteDialog(item);
  };

  const handleCloseDeleteDialog = () => {
    setItemForDeleteDialog(null);
  };

  const addNewButton = (
    <Button
      style={{ margin: '24px' }}
      color={'primary'}
      variant={'contained'}
      onClick={handleOpenAddForm}
    >
      {t('userLabels.addOne')}
    </Button>
  );

  const actions = [
    {
      title: 'form.delete',
      icon: <DeleteForever />,
      callback: handleOpenDeleteDialog,
    },
    {
      title: 'form.update',
      icon: <Edit />,
      callback: handleOpenEditForm,
    },
  ];

  const cols: DataGridCol<UserLabel>[] = useMemo(() => ([
    createCol(t('userLabels.table.title'), 'name', (name, item) => (
      <span className={'tw-inline-flex tw-items-center tw-gap-8'}>
        <UserLabelColorSwatch color={item.color} />
        {name}
      </span>
    )),
    createCol(t('userLabels.table.userCount'), 'user_count'),
  ]), [t]);

  return (
    <>
      <Card>
        <CardHeader
          className={'gray-header'}
          title={t('userLabels.title')}
          action={
            <div>
              <Tooltip title={t('userLabels.addOne')}>
                <Button onClick={handleOpenAddForm} color={'primary'} variant={'contained'}>
                  <PlaylistAdd /> {t('form.add')}
                </Button>
              </Tooltip>
            </div>
          }
        />
        <CardContent>
          <DataGrid
            cols={cols}
            state={dataGridState}
            setState={setDataGridState}
            itemsState={itemsState}
            mode={DataGridMode.CLIENT}
            emptyListMessage={
              <>
                <p>{t('userLabels.emptyList')}</p>
                {addNewButton}
              </>
            }
            emptySearchMessage={
              <>
                <p>{t('userLabels.emptySearch')}</p>
                {addNewButton}
              </>
            }
            actions={actions}
          />
        </CardContent>
      </Card>
      {isOpenAddForm && (
        <UserLabelSaveForm
          onSuccess={fetchItems}
          onClose={handleCloseAddForm}
        />
      )}
      {itemForEditForm && (
        <UserLabelSaveForm
          item={itemForEditForm}
          onSuccess={fetchItems}
          onClose={handleCloseEditForm}
        />
      )}
      {itemForDeleteDialog && (
        <UserLabelDeleteForm
          item={itemForDeleteDialog}
          onSuccess={fetchItems}
          onClose={handleCloseDeleteDialog}
        />
      )}
    </>
  );
};
